import { FC } from 'react';
import { IQueryParamDrivenDrawerContentProvider } from '../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { QueryParamDrivenDrawerPage } from '../components/CareMapDrawerPage';
import { CommentList } from '../comments/CommentList';
import { CommunityPost } from './CommunityPost';
import { CommunityPostFooter } from './CommunityPostFooter';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { useSubscription } from '@meraki-internal/state';
import { BlockUntilActiveCaremap } from '../ActiveCareMapProvider';
import { ReactionsRow } from './reactions/ReactionsRow';
import { Overview } from '../notes/view-note/Overview';
import { Participants } from '../notes/view-note/Participants';
import { SmartTasks } from '../notes/view-note/SmartTasks';
import { Attachments } from '../notes/view-note/Attachments';
import { DetailedSummary } from '../notes/view-note/DetailedSummary';

interface IViewCommunityPostDrawerProps {
    postId: string;
    addComment?: boolean;
};

export class ViewCommunityPostWithQueryParamDrawer implements IQueryParamDrivenDrawerContentProvider<IViewCommunityPostDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IViewCommunityPostDrawerProps>){}

    mode = 'view-community-post';

    Component = ViewCommunityPostDrawerView;

    options = {
        allowBackdropClose: true
    };

    open = (props: IViewCommunityPostDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };

    getUrl = (props: IViewCommunityPostDrawerProps) => {
        return this.drawerHelper.getUrl({ mode: this.mode, ...props });
    };
};

export const ViewCommunityPostDrawerContent: FC<IViewCommunityPostDrawerProps> = ({ postId }) => {
    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const model = notesFactory.forActiveCaremap();
    const post = model.getNote(postId);

    if (!post) {
        return;
    }

    return (
        <>
            <CommunityPost fullContent post={post} />

            <Overview note={post} />

            <Participants note={post} />

            <DetailedSummary note={post} />

            <SmartTasks note={post} />

            {post.attachments.length > 0 && (
                <Attachments note={post} />
            )}

            <ReactionsRow post={post} />
            <CommentList post={post} />
        </>
    );
};

export const ViewCommunityPostDrawerView: FC<IViewCommunityPostDrawerProps> = ({ postId, addComment }) => {
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    return (
        <QueryParamDrivenDrawerPage
            title="View Post"
            hasBack={false}
            data-type="view-post-drawer"
            onClose={drawerHelper.close}
            exteriorFooter={
                <BlockUntilActiveCaremap>
                    <CommunityPostFooter
                        parentNoteId={postId}
                        autoFocus={addComment}
                    />
                </BlockUntilActiveCaremap>
            }
        >
            <BlockUntilActiveCaremap>
                <ViewCommunityPostDrawerContent
                    postId={postId}
                />
            </BlockUntilActiveCaremap>
        </QueryParamDrivenDrawerPage>
    );
};
